// Neutral monsters (Бегемот, Грифон): one glTF rig per den, driven every frame by GameView.monsters.
// Their skeleton is a quadruped one outside the hero bone contract and their clips are fixed (Idle, Walk,
// Attack, Hit, Death), so they get their own small clip driver instead of heroView's playClip/LOOPING.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { adoptMesh, release, setDying } from './engrave.js';
import { loadModel } from './heroView.js';
import type { MonsterKind, MonsterState } from '../core/monsters.js';
import type { GameView, ViewMonster } from './gameView.js';

/** View-only per-kind dressing: model and its scale, hover/target ring radius, plate height, and the half
    body length input.ts spreads its pick points along. */
export const MONSTER_LOOK: Record<MonsterKind, { model: string; scale: number; ring: number; plateY: number; half: number }> = {
  behemoth: { model: 'assets/behemoth_v1.gltf.json', scale: 1, ring: 2.2, plateY: 3.6, half: 1.8 },
  griffin: { model: 'assets/griffin_v1.gltf.json', scale: 1.4, ring: 2.3, plateY: 3.8, half: 1.8 },
};

const LOOPING = new Set(['Idle', 'Walk']);
const DEATH_HOLD = 0.8, SINK = 1.2;  // after the Death clip: lie still, then sink into the page like a hero

export type MonsterRig = {
  id: number;
  kind: MonsterKind;
  root: THREE.Group;
  mixer: THREE.AnimationMixer | null;
  actions: Record<string, THREE.AnimationAction> | null;
  current: THREE.AnimationAction | null;
  yaw: number;
  lastHp: number;
  lastState: MonsterState | null;
  lastStateT: number;
  /** Seconds since the Death clip started, null while not dying. */
  dying: number | null;
  /** Set on match stop: a model still loading then must not register its meshes with the engraver. */
  disposed: boolean;
};

/** A `monsterSlain` seen recently, waiting for its monster to leave `view.monsters`. Online the event is
    surfaced at once while the monster's disappearance comes through the delayed interpolation timeline
    (latency + up to 200 ms), so the two rarely land on the same frame. */
type PendingKill = { x: number; z: number; age: number };
const KILL_WINDOW = 1.0;

export type MonsterViews = { rigs: MonsterRig[]; kills: PendingKill[] };

const loader = new GLTFLoader();

function createMonsterRig(scene: THREE.Scene, id: number, kind: MonsterKind): MonsterRig {
  const rig: MonsterRig = {
    id, kind, root: new THREE.Group(), mixer: null, actions: null, current: null,
    yaw: 0, lastHp: 0, lastState: null, lastStateT: 0, dying: null, disposed: false,
  };
  rig.root.visible = false;
  scene.add(rig.root);
  loadModel(loader, MONSTER_LOOK[kind].model, (gltf) => {
    if (rig.disposed) return;
    const model = gltf.scene as THREE.Group;
    model.scale.setScalar(MONSTER_LOOK[kind].scale);
    // No team_* materials on a monster: every mesh lands on the neutral 'unit' mask, whatever team is passed.
    model.traverse((o: THREE.Object3D) => { if ((o as THREE.Mesh).isMesh) adoptMesh(o as THREE.Mesh, 'a'); });
    const mixer = new THREE.AnimationMixer(model), actions: Record<string, THREE.AnimationAction> = {};
    for (let clip of gltf.animations as THREE.AnimationClip[]) {
      // Hit is layered additively on top of whatever runs, so a flinch never cancels a windup.
      const additive = clip.name === 'Hit';
      if (additive) {
        // makeClipAdditive rewrites key values in place, and the exporter shares identical constant tracks'
        // arrays across clips — without a deep copy it zeroes those keys in Idle/Walk too (bones collapse).
        clip = new THREE.AnimationClip(clip.name, clip.duration, clip.tracks.map(t => {
          const c = t.clone(); c.times = t.times.slice(); c.values = t.values.slice(); return c;
        }));
        THREE.AnimationUtils.makeClipAdditive(clip);
      }
      const a = mixer.clipAction(clip, undefined, additive ? THREE.AdditiveAnimationBlendMode : THREE.NormalAnimationBlendMode);
      if (!LOOPING.has(clip.name)) { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = clip.name !== 'Hit'; }
      actions[clip.name] = a;
    }
    rig.root.add(model);
    rig.mixer = mixer;
    rig.actions = actions;
    rig.current = null;
    fadeTo(rig, rig.dying !== null ? 'Death' : 'Idle', 0, true);
  }, (err: any) => console.warn('Monster model failed to load:', MONSTER_LOOK[kind].model, err && err.message || err));
  return rig;
}

function fadeTo(rig: MonsterRig, name: string, fade: number, restart = false) {
  const next = rig.actions?.[name];
  if (!next || (rig.current === next && !restart)) return;
  const prev = rig.current;
  next.reset().setEffectiveWeight(1);
  // Restarting the clip already on screen must not dip its weight to 0 (that blends toward the bind pose).
  if (prev !== next && fade > 0) next.fadeIn(fade);
  next.play();
  if (prev && prev !== next) { if (fade > 0) prev.fadeOut(fade); else prev.stop(); }
  rig.current = next;
}

/** Base clip for a live monster. Attack is timed to the sim, not fired and forgotten: see syncAttack. */
function clipFor(rig: MonsterRig, m: ViewMonster): string {
  if (m.state === 'windup') return 'Attack';
  const attack = rig.actions?.Attack;
  // recover: let the swing play out (its tail runs past the impact), then settle.
  if (m.state === 'recover' && attack && rig.current === attack && attack.isRunning()) return 'Attack';
  if (m.state === 'return') return 'Walk';
  return m.moving ? 'Walk' : 'Idle';
}

/* The windup lasts exactly as long as the clip's lead-in to its hit frame (0.95 s vs 0.958 s), so the
   clip clock simply IS the state clock: restart on each new windup, and pull it back if it drifts (a
   hitch, an online snapshot correcting stateT). */
function syncAttack(rig: MonsterRig, m: ViewMonster) {
  const attack = rig.actions?.Attack;
  if (!attack) return;
  const fresh = rig.lastState !== 'windup' || m.stateT < rig.lastStateT - 0.2;
  if (fresh || rig.current !== attack) fadeTo(rig, 'Attack', 0.1, true);
  if (fresh || Math.abs(attack.time - m.stateT) > 0.12) attack.time = Math.min(m.stateT, attack.getClip().duration);
}

function flinch(rig: MonsterRig) {
  const hit = rig.actions?.Hit;
  if (hit) hit.reset().setEffectiveWeight(0.8).play();
}

function startDeath(rig: MonsterRig) {
  rig.dying = 0;
  rig.root.visible = true;
  rig.actions?.Hit?.stop();
  fadeTo(rig, 'Death', 0.15, true);
}

function endDeath(rig: MonsterRig) {
  if (rig.dying === null) return;
  rig.dying = null;
  setDying(rig.root, null);
  rig.root.position.y = 0;
  rig.root.scale.set(1, 1, 1);
}

function driveDeath(rig: MonsterRig, dt: number) {
  rig.dying = (rig.dying as number) + dt;
  const clip = rig.actions?.Death?.getClip().duration ?? 1.5;
  const sink = Math.max(0, Math.min(1, (rig.dying - clip - DEATH_HOLD) / SINK));
  if (sink > 0) {
    setDying(rig.root, 0.03 + sink * 0.97);
    rig.root.position.y = -sink * 0.9;
    rig.root.scale.set(1 + sink * 0.2, Math.max(0.01, 1 - sink * 0.85), 1 + sink * 0.2);
  }
  if (sink >= 1) { endDeath(rig); rig.root.visible = false; }
}

const angDiff = (a: number, b: number) => Math.atan2(Math.sin(b - a), Math.cos(b - a));

export function createMonsterViews(scene: THREE.Scene, kinds: readonly MonsterKind[]): MonsterViews {
  return { rigs: kinds.map((kind, id) => createMonsterRig(scene, id, kind)), kills: [] };
}

/** One frame for every den. Reads this frame's events (call before GameView.clearEvents). A `monsterSlain`
    fx carries only a position: it is remembered for KILL_WINDOW and matched to the nearest rig that goes
    from shown to absent within that window — only then does Death play; any other disappearance is fog. */
export function driveMonsters(mv: MonsterViews, view: GameView, dt: number) {
  const present = new Map(view.monsters.map(m => [m.id, m]));
  const team = view.player?.team;
  for (const k of mv.kills) k.age += dt;
  for (let i = mv.kills.length - 1; i >= 0; i--) if (mv.kills[i]!.age > KILL_WINDOW) mv.kills.splice(i, 1);
  for (const e of view.events) {
    if (e.type !== 'fx' || e.name !== 'monsterSlain') continue;
    if (team && !view.inSight(team, e.pos)) continue;
    mv.kills.push({ x: e.pos.x, z: e.pos.z, age: 0 });
  }

  for (const rig of mv.rigs) {
    const m = present.get(rig.id);
    if (m) {
      if (!rig.root.visible || rig.dying !== null) {
        endDeath(rig);
        rig.root.visible = true;
        rig.yaw = m.yaw;
        rig.lastHp = m.hp;
        rig.lastState = null;
        rig.actions?.Hit?.stop();
        fadeTo(rig, 'Idle', 0, true);
      }
      rig.root.position.set(m.pos.x, 0, m.pos.z);
      rig.yaw += angDiff(rig.yaw, m.yaw) * (1 - Math.exp(-dt * 14));
      rig.root.rotation.y = rig.yaw;
      if (m.state === 'windup') syncAttack(rig, m);
      else fadeTo(rig, clipFor(rig, m), m.state === 'recover' ? 0.25 : 0.2);
      if (m.hp < rig.lastHp - 0.01) flinch(rig);
      rig.lastHp = m.hp;
      rig.lastState = m.state;
      rig.lastStateT = m.stateT;
    } else if (rig.dying !== null) {
      driveDeath(rig, dt);
    } else if (rig.root.visible) {
      const k = takeKill(mv.kills, rig);
      if (k) { rig.root.position.x = k.x; rig.root.position.z = k.z; startDeath(rig); }
      else rig.root.visible = false;
    }
    if (rig.root.visible && rig.mixer) rig.mixer.update(dt);
  }
}

function takeKill(kills: PendingKill[], rig: MonsterRig): PendingKill | null {
  let best = -1, bestD = 8;
  kills.forEach((k, i) => {
    const d = Math.hypot(rig.root.position.x - k.x, rig.root.position.z - k.z);
    if (d < bestD) { bestD = d; best = i; }
  });
  return best < 0 ? null : kills.splice(best, 1)[0]!;
}

export function disposeMonsterViews(scene: THREE.Scene, mv: MonsterViews) {
  for (const rig of mv.rigs) {
    rig.disposed = true;
    scene.remove(rig.root);
    rig.mixer?.stopAllAction();
    release(rig.root, { geometry: false });  // geometry belongs to heroView's model cache
  }
  mv.kills.length = 0;
}
